import { useVirtualizer } from "@tanstack/react-virtual";
import { AxiosResponse } from "axios";
import moment from "moment";
import { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  deleteReservationsAsync,
  deleteVisitorsAsync,
  getContractAsync,
  getMemberContractManageListAsync,
  getReservationListAsync,
  getVisitorListAsync,
  patchMemberContractManageDeleteAsync,
} from "src/api/contract/contract-api";
import { ContractModel, ContractReservationListParams, MemberContractManageModel } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { ApiResponse } from "src/api/public-types";
import { RvReservationStatusCode } from "src/api/reservation/reservation-type";
import { ContractVisitorId, VisitorListParams, VisitStatus } from "src/api/visitor/visitor-types";
import { ConfirmModal, Spinner } from "src/components";
import { BaseCheckbox } from "src/components/BaseCheckbox";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import useExcelDownload, { ExcelColumn } from "src/pages/mypage/contract/hooks/useExcelDownload";
import { MEMBER_SORT } from "src/pages/mypage/contract/user/user-types";
import { useMemberInfoState } from "src/recoil/member/hook";
import { getMypageTitle } from "src/utils/app-Info";
import { NO_END_TIME, numberToStringWithComma, YmdFormat } from "src/utils/common-util";
import * as xlsx from "xlsx";

// ════════ 상수 ════════

const EMPTY_MARK = "-";

/** Step2 안내 문구에 노출하는 권고 인원. 이 수를 넘겨도 막지 않는다 */
const RECOMMENDED_UPLOAD_ROWS = 100;

/** 실제로 업로드를 거절하는 상한 */
const MAX_UPLOAD_ROWS = 10_000;

/** 계약 이용자 목록(TCU46) 페이지 순회 크기 */
const MEMBER_PAGE_SIZE = 100;

/** 판정은 잔여 건수만 필요하므로 목록은 최소로 받고 totalElements 만 읽는다 */
const COUNT_PAGE_SIZE = 1;

/** 잔여 해소는 건을 실제로 지워야 하므로 목록을 받아온다 */
const RESOLVE_PAGE_SIZE = 100;

/** 오래 도는 루프라 요청 하나가 매달리면 전체가 멈춘다. axios 기본값은 무제한 대기다 */
const REQUEST_TIMEOUT_MS = 15_000;

// 에러는 executeAsync 가 아니라 반환값으로 직접 판별한다
const REQUEST_OPTIONS = { noHandleError: true, timeout: REQUEST_TIMEOUT_MS };

// 잔여 방문자 초대는 사용 + 대기를 합산한다. 초대 경로는 구분하지 않는다
const VISIT_STATUSES = [VisitStatus.VISIT_USE, VisitStatus.VISIT_PENDING];

const RESERVATION_STATUS_CODES = [RvReservationStatusCode.ACCEPTED, RvReservationStatusCode.ACKNOWLEDGED, RvReservationStatusCode.INCOMPLETE];

// 사유 배너가 붙으면 행이 더 길어진다. 실제 높이는 measureElement 가 재므로 첫 배치만 어림잡으면 된다
const ROW_ESTIMATED_HEIGHT = 130;

// 마지막 삭제 건이 끝나고 진행 요약 토스트가 사라지기까지 (QA-3719)
const SUMMARY_TOAST_LINGER_MS = 1_000;

// ════════ 화면 단계 ════════

const USER_DELETE_PHASE = {
  IDLE: "IDLE", // 업로드 전
  VALIDATING: "VALIDATING", // 1단계 판정 순회 중
  SELECTING: "SELECTING", // 2단계 대상 선택
  DELETING: "DELETING", // 3단계 순차 삭제
  RESULT: "RESULT", // 결과
} as const;

type UserDeletePhase = typeof USER_DELETE_PHASE[keyof typeof USER_DELETE_PHASE];

// ════════ 행 ════════

/**
 * 행 상태. 화면 단계와는 별개의 축이라 같은 DELETING 화면 안에 여러 행 상태가 공존한다.
 *
 * 체크 상태를 따로 두지 않는다 — 체크를 풀면 SELECTABLE → EXCLUDED, 다시 켜면 되돌아간다.
 * 삭제 대상은 곧 SELECTABLE 전부여서 별도 선택 집합이 필요 없다.
 */
const USER_DELETE_ROW_STATE = {
  SELECTABLE: "SELECTABLE", // 판정 통과. 체크됨
  EXCLUDED: "EXCLUDED", // 사용자가 체크를 풂
  RESOLVABLE: "RESOLVABLE", // 잔여 예약·방문자만 걸림. 개별 삭제로 풀 수 있음
  BLOCKED: "BLOCKED", // 계약자·계약 미포함·이미 삭제·판정 조회 실패
  WAITING: "WAITING", // 일괄 삭제 대기
  PROCESSING: "PROCESSING", // 삭제 요청 중
  DELETED: "DELETED", // 삭제 완료
  FAILED: "FAILED", // 삭제 실패
} as const;

type UserDeleteRowState = typeof USER_DELETE_ROW_STATE[keyof typeof USER_DELETE_ROW_STATE];

const USER_DELETE_REASON = {
  CONTRACTOR: "CONTRACTOR",
  SELF_ACCOUNT: "SELF_ACCOUNT",
  NOT_IN_CONTRACT: "NOT_IN_CONTRACT",
  NICKNAME_MISMATCH: "NICKNAME_MISMATCH",
  PHONE_MISMATCH: "PHONE_MISMATCH",
  EMAIL_MISMATCH: "EMAIL_MISMATCH",
  ALREADY_DELETED: "ALREADY_DELETED",
  REMAINING_RESERVATION: "REMAINING_RESERVATION",
  REMAINING_VISITOR: "REMAINING_VISITOR",
  EXCLUDED: "EXCLUDED",
  PROCESS_ERROR: "PROCESS_ERROR",
} as const;

type UserDeleteReason = typeof USER_DELETE_REASON[keyof typeof USER_DELETE_REASON];

type UserDeleteReasonItem = {
  reason: UserDeleteReason;
  count?: number;
};

/**
 * 상태는 하나지만 사유는 여러 개가 붙는다 — 기획이 "복수 사유 존재 시 사유 전체 노출" 이고
 * 결과 엑셀 사유 컬럼도 복수 값을 요구한다.
 *
 * reasons 에는 판정으로만 알 수 있는 사유를 담는다. 삭제 대상 제외(EXCLUDED)와 삭제 실패
 * (PROCESS_ERROR)는 상태만 보면 알 수 있으므로 저장하지 않고 그릴 때 파생한다.
 */
type UserDeleteRow = {
  rowId: string; // 엑셀 행 번호
  partnerId: string;
  contractManageId: string;
  contractMemberId: string;
  memberNo: string;
  nickname: string;
  phoneNumber: string;
  email: string;
  state: UserDeleteRowState;
  reasons: UserDeleteReasonItem[];
  /**
   * 삭제 API 에 보낼 이용자 ID. **엑셀 값이 아니라 4키 대조로 찾아낸 서버 응답값이다.**
   *
   * 엑셀 셀은 사용자가 만진 문자열이라 타입도 자릿수도 보장되지 않는다. 명세가 `long` 을
   * 요구하므로 서버가 준 값을 그대로 되돌려준다. 대조에 실패한 행은 이 값이 없고,
   * 값이 없으면 삭제 요청을 만들지 않는다 — 잘못된 대상이 API 까지 갈 경로가 없다.
   */
  deleteTargetId?: number;
};

type UserDeleteSummary = {
  total: number; // 명단 전체
  selectableCount: number; // 삭제 대상 (하단 CTA 활성 판단)
  deletedCount: number; // 결과 화면 칩
};

/** 대조를 통과해 삭제 요청을 만들 수 있는 행 */
type DeletableRow = UserDeleteRow & { deleteTargetId: number };

const EMPTY_SUMMARY: UserDeleteSummary = { total: 0, selectableCount: 0, deletedCount: 0 };

// 일괄 삭제 루프가 한 번이라도 손댄 상태
const ATTEMPTED_STATES: UserDeleteRowState[] = [
  USER_DELETE_ROW_STATE.WAITING,
  USER_DELETE_ROW_STATE.PROCESSING,
  USER_DELETE_ROW_STATE.DELETED,
  USER_DELETE_ROW_STATE.FAILED,
];

// ════════ 행 저장소 ════════

/**
 * 명단과 행별 상태를 들고 있는 저장소. 화면은 이걸 소유하지 않고 구독만 한다.
 *
 * **상태를 갈아끼울 줄만 안다.** 판정 규칙·삭제 대상 선별·체크 토글은 전부 밖에서 계산해
 * 결과만 넣는다. 저장소가 "계약자면 삭제 불가" 같은 기획 규칙을 알기 시작하면 규칙이 두 군데로
 * 흩어진다.
 *
 * 목록 구독 채널은 두지 않는다. 명단이 바뀌는 건 업로드 한 번뿐이고 그 순간은 화면 단계가
 * IDLE → VALIDATING 으로 넘어가며 이미 알려준다.
 */
const createRowStore = () => {
  let rowIds: string[] = [];
  let rowById = new Map<string, UserDeleteRow>();
  let summary: UserDeleteSummary = EMPTY_SUMMARY;

  const rowListeners = new Map<string, Set<() => void>>();
  const summaryListeners = new Set<() => void>();

  const notifyRow = (rowId: string) => {
    rowListeners.get(rowId)?.forEach((listener) => listener());
  };

  // 집계는 상태에서 기계적으로 파생된다. 값이 그대로면 같은 객체를 유지해 헛된 리렌더를 막는다
  const refreshSummary = () => {
    let selectableCount = 0;
    let deletedCount = 0;

    rowById.forEach((row) => {
      if (row.state === USER_DELETE_ROW_STATE.SELECTABLE) {
        selectableCount += 1;
      }
      if (row.state === USER_DELETE_ROW_STATE.DELETED) {
        deletedCount += 1;
      }
    });

    const isSame = summary.total === rowIds.length && summary.selectableCount === selectableCount && summary.deletedCount === deletedCount;
    if (isSame) {
      return;
    }

    summary = { total: rowIds.length, selectableCount, deletedCount };
    summaryListeners.forEach((listener) => listener());
  };

  return {
    getRowIds: () => rowIds,
    getRow: (rowId: string) => rowById.get(rowId),
    getSummary: () => summary,

    subscribeRow: (rowId: string, listener: () => void) => {
      const listeners = rowListeners.get(rowId) || new Set<() => void>();
      listeners.add(listener);
      rowListeners.set(rowId, listeners);

      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          rowListeners.delete(rowId);
        }
      };
    },

    subscribeSummary: (listener: () => void) => {
      summaryListeners.add(listener);

      return () => {
        summaryListeners.delete(listener);
      };
    },

    /** 명단 적재. 업로드 때 한 번만 부른다 */
    setRows: (rows: UserDeleteRow[]) => {
      const previousRowIds = rowIds;

      rowIds = rows.map((row) => row.rowId);
      rowById = new Map(rows.map((row) => [row.rowId, row]));

      refreshSummary();
      previousRowIds.forEach(notifyRow);
      rowIds.forEach(notifyRow);
    },

    setRow: (rowId: string, next: UserDeleteRow) => {
      if (!rowById.has(rowId)) {
        return;
      }

      rowById.set(rowId, next);
      refreshSummary();
      notifyRow(rowId);
    },

    /** 여러 행을 한 번에. setRow 를 N번 부르면 통지가 N번 나가 명단 전체가 출렁인다 */
    patchRows: (nextByRowId: Map<string, UserDeleteRow>) => {
      nextByRowId.forEach((next, rowId) => {
        if (rowById.has(rowId)) {
          rowById.set(rowId, next);
        }
      });

      refreshSummary();
      nextByRowId.forEach((_, rowId) => notifyRow(rowId));
    },
  };
};

type RowStore = ReturnType<typeof createRowStore>;

/** 행 하나만 구독한다. 삭제 루프가 한 건을 갈아끼우면 그 행만 다시 그려진다 */
const useRow = (store: RowStore, rowId: string) => {
  const [row, setRow] = useState(() => store.getRow(rowId));

  useEffect(() => {
    // 첫 렌더와 구독 사이에 저장소가 바뀌었을 수 있다. 이 줄이 없으면 그 갱신을 조용히 놓친다 —
    // 판정·삭제 루프가 도는 중에 가상 스크롤이 행을 새로 붙이는 순간 실제로 발생한다. 지우지 말 것
    setRow(store.getRow(rowId));

    return store.subscribeRow(rowId, () => setRow(store.getRow(rowId)));
  }, [store, rowId]);

  return row;
};

const useSummary = (store: RowStore) => {
  const [summary, setSummary] = useState(() => store.getSummary());

  useEffect(() => {
    // useRow 와 같은 이유
    setSummary(store.getSummary());

    return store.subscribeSummary(() => setSummary(store.getSummary()));
  }, [store]);

  return summary;
};

// ════════ 행 상태 조회 ════════

const hasReason = (row: UserDeleteRow, reason: UserDeleteReason) => {
  return row.reasons.some((item) => item.reason === reason);
};

/** 이번에 지웠든 전부터 지워져 있었든 화면에는 '삭제완료' 로 보인다 */
const showsDeletedMark = (row: UserDeleteRow) => {
  return row.state === USER_DELETE_ROW_STATE.DELETED || hasReason(row, USER_DELETE_REASON.ALREADY_DELETED);
};

// 이 사유가 붙으면 개별 삭제로도 못 푼다. 잔여 예약·방문자와 판정 조회 실패는 여기 없다 —
// 전자는 지우면 되고, 후자는 모달을 열 때 잔여를 다시 읽으므로 그 자체가 재시도다
const FINAL_REASONS: UserDeleteReason[] = [
  USER_DELETE_REASON.CONTRACTOR,
  USER_DELETE_REASON.SELF_ACCOUNT,
  USER_DELETE_REASON.NOT_IN_CONTRACT,
  USER_DELETE_REASON.NICKNAME_MISMATCH,
  USER_DELETE_REASON.PHONE_MISMATCH,
  USER_DELETE_REASON.EMAIL_MISMATCH,
  USER_DELETE_REASON.ALREADY_DELETED,
];

/** 잔여 예약·방문자가 걸린 행. 기획상 이쪽이 재시도보다 우선한다 */
const hasRemainingReason = (row: UserDeleteRow) => {
  return row.reasons.some(({ reason }) => {
    return reason === USER_DELETE_REASON.REMAINING_RESERVATION || reason === USER_DELETE_REASON.REMAINING_VISITOR;
  });
};

const canResolveIndividually = (row: UserDeleteRow) => {
  return !row.reasons.some(({ reason }) => FINAL_REASONS.includes(reason));
};

/** 개별 삭제 버튼을 그릴지. 2단계와 결과 화면의 규칙이 다르다 */
const hasIndividualButton = (row: UserDeleteRow, phase: UserDeletePhase) => {
  if (showsDeletedMark(row)) {
    return false;
  }
  if (phase === USER_DELETE_PHASE.SELECTING) {
    // 판정에서 막힌 건 중 풀 수 있는 것만 열어준다. 계약자 등은 아예 노출하지 않는다
    const isJudgeBlocked = row.state === USER_DELETE_ROW_STATE.RESOLVABLE || row.state === USER_DELETE_ROW_STATE.BLOCKED;

    return isJudgeBlocked && canResolveIndividually(row);
  }
  if (phase === USER_DELETE_PHASE.RESULT) {
    // 개별 삭제로 풀 수 없는 사유(계약자 등)는 사유 배너만 남긴다. 비활성 버튼도 내지 않는다 —
    // 기획 사유별 활성화 표는 비활성 노출이었으나 시안이 미노출로 덮었다 (QA-3719)
    if (!canResolveIndividually(row)) {
      return false;
    }

    return row.state !== USER_DELETE_ROW_STATE.WAITING && row.state !== USER_DELETE_ROW_STATE.PROCESSING;
  }

  return false;
};

/**
 * 사유를 늘어놓는 순서. 한 행에 사유가 여러 개 붙으므로 **판정이 훑은 순서가 아니라 이 순서로 낸다** —
 * 순서가 코드 흐름을 따라가면 판정 로직을 손댈 때마다 화면 문구 순서가 딸려 바뀐다.
 *
 * 값은 기획서·다국어 문서의 사유 목록 순서다.
 *
 * 사유가 늘면 여기서 컴파일 에러가 나므로 순서를 정하지 않고 지나칠 수 없다.
 */
const REASON_ORDER: Record<UserDeleteReason, number> = {
  [USER_DELETE_REASON.CONTRACTOR]: 1,
  [USER_DELETE_REASON.SELF_ACCOUNT]: 2,
  [USER_DELETE_REASON.REMAINING_RESERVATION]: 3,
  [USER_DELETE_REASON.REMAINING_VISITOR]: 4,
  [USER_DELETE_REASON.NOT_IN_CONTRACT]: 5,
  [USER_DELETE_REASON.NICKNAME_MISMATCH]: 6,
  [USER_DELETE_REASON.PHONE_MISMATCH]: 7,
  [USER_DELETE_REASON.EMAIL_MISMATCH]: 8,
  [USER_DELETE_REASON.ALREADY_DELETED]: 9,
  [USER_DELETE_REASON.PROCESS_ERROR]: 10,
  [USER_DELETE_REASON.EXCLUDED]: 11,
};

const sortReasons = (reasons: UserDeleteReasonItem[]) => {
  return [...reasons].sort((a, b) => REASON_ORDER[a.reason] - REASON_ORDER[b.reason]);
};

/**
 * 저장된 판정 사유 + 상태에서 파생되는 사유.
 *
 * `삭제 대상 제외` 는 결과 화면에서만 붙인다. 2단계에서 체크를 푸는 순간 사유 배너가 뜨면
 * 방금 사용자가 한 행동을 오류처럼 되돌려주는 꼴이다 — 그건 삭제를 마친 뒤에 알려줄 결과다.
 */
const toDisplayReasons = (row: UserDeleteRow, phase: UserDeletePhase): UserDeleteReasonItem[] => {
  const derived: UserDeleteReasonItem[] = [];

  if (row.state === USER_DELETE_ROW_STATE.EXCLUDED && phase === USER_DELETE_PHASE.RESULT) {
    derived.push({ reason: USER_DELETE_REASON.EXCLUDED });
  }

  // 실패 후 재조회로 잔여가 밝혀졌으면 그게 사유다. '삭제 처리 오류' 는 이유를 모를 때만 쓴다
  if (row.state === USER_DELETE_ROW_STATE.FAILED && !hasRemainingReason(row)) {
    derived.push({ reason: USER_DELETE_REASON.PROCESS_ERROR });
  }

  // 화면 배너와 결과 엑셀이 같은 함수를 타므로 여기서 한 번 세우면 두 곳의 순서가 갈리지 않는다
  return sortReasons([...row.reasons, ...derived]);
};

// ════════ API 응답 헬퍼 ════════

/**
 * executeAsync 는 실패해도 throw 하지 않는다 — 성공하면 응답, HTTP 에러면 _error.response,
 * 네트워크 실패면 undefined 를 돌려준다. try/catch 로 감싸면 실패가 전부 성공으로 기록된다.
 */
const isResponseOk = <T,>(response?: AxiosResponse<ApiResponse<T>>): boolean => {
  if (!response) {
    return false;
  }
  if (response.data?.meta?.errorCode) {
    return false;
  }

  const status = Number(response.status || 0);

  return status >= 200 && status <= 299;
};

/**
 * 전체 건수. 실패하면 null 이며 **0건과 반드시 구분한다** — 조회 실패를 0으로 뭉개면
 * "잔여 없음"으로 오판한다. 서버가 문자열로 주는 경우가 있어 Number 로 맞춘다.
 */
const readTotalCount = <T,>(response?: AxiosResponse<ApiResponse<T>>): number | null => {
  if (!isResponseOk(response)) {
    return null;
  }

  return Number(response?.data?.meta?.pageMeta?.totalElements || 0);
};

/**
 * page 0부터 마지막 페이지까지 순회해 모은다. **끝까지 못 읽으면 null 이다** — 모으다 만 목록을
 * 성공으로 돌려주면 명단 대조에서 있는 사람을 "계약에 없는 이용자" 로 오판한다.
 */
const fetchAllPages = async <D, T>(
  readPage: (page: number) => Promise<AxiosResponse<ApiResponse<D>> | undefined>,
  selectItems: (data?: D) => T[],
  signal: AbortSignal,
): Promise<T[] | null> => {
  const items: T[] = [];

  let page = 0;
  let totalPages = 1; // 첫 응답을 받기 전까지는 최소 1장으로 본다

  while (page < totalPages) {
    if (signal.aborted) {
      return null;
    }

    const response = await readPage(page);
    if (!isResponseOk(response)) {
      return null;
    }

    const body = response?.data;
    items.push(...selectItems(body?.data));
    totalPages = Number(body?.meta?.pageMeta?.totalPages || 0);
    page += 1;
  }

  return items;
};

// ════════ 값 정규화 ════════

const toText = (value: unknown): string => {
  return value === null || value === undefined ? "" : String(value).trim();
};

const orEmptyMark = (value?: string | null) => {
  return value ? value : EMPTY_MARK;
};

/**
 * 양식이 빈 값을 `-` 로 채워 내려주므로 되읽을 때 원래의 빈 값으로 되돌린다.
 *
 * `-` 를 그대로 들고 있으면 회원번호가 조회 필터로 나가 `organizerMemberNo=-` 가 서버까지 간다.
 * `-` 는 표시 형식일 뿐이고 행이 들고 있어야 할 값은 서버와 같은 빈 문자열이다.
 */
const fromEmptyMark = (value: string) => {
  return value === EMPTY_MARK ? "" : value;
};

// ════════ 판정 (저장소 밖 순수 함수) ════════

type Remaining = {
  reservationCount: number;
  visitorCount: number;
};

/** 회원 한 명의 잔여 건수. isFailed 면 조회 자체가 실패한 것이라 0과 다르다 */
type MemberRemaining = Remaining & {
  isFailed: boolean;
};

/** 조회하지 않고 0건으로 확정한 잔여 — 회원번호가 없는 이용자 */
const NO_REMAINING: MemberRemaining = { reservationCount: 0, visitorCount: 0, isFailed: false };

/**
 * 방금 읽은 잔여 건수로 행의 잔여 사유를 다시 쓴다.
 *
 * 모달에서 잔여 예약을 지웠는데 목록에 "공용공간 예약 3건" 이 그대로 남아 있으면 화면이
 * 거짓말을 한다. 잔여가 모두 사라졌으면 판정을 통과한 것이므로 삭제 대상으로 되돌린다.
 */
const withRemainingReasons = (row: UserDeleteRow, remaining: Remaining): UserDeleteRow => {
  const kept = row.reasons.filter(({ reason }) => {
    return reason !== USER_DELETE_REASON.REMAINING_RESERVATION && reason !== USER_DELETE_REASON.REMAINING_VISITOR;
  });

  if (remaining.reservationCount > 0) {
    kept.push({ reason: USER_DELETE_REASON.REMAINING_RESERVATION, count: remaining.reservationCount });
  }
  if (remaining.visitorCount > 0) {
    kept.push({ reason: USER_DELETE_REASON.REMAINING_VISITOR, count: remaining.visitorCount });
  }

  // 상태를 되돌리는 건 판정으로 RESOLVABLE 이 된 행뿐이다. 삭제를 시도했던 행의 결과는 건드리지 않는다
  if (row.state !== USER_DELETE_ROW_STATE.RESOLVABLE) {
    return { ...row, reasons: kept };
  }

  return {
    ...row,
    reasons: kept,
    state: kept.length === 0 ? USER_DELETE_ROW_STATE.SELECTABLE : USER_DELETE_ROW_STATE.RESOLVABLE,
  };
};

// 기획대로 네 개가 모두 일치해야 같은 사람이다
const toMatchKey = (partnerId: unknown, contractManageId: unknown, contractMemberId: unknown, memberNo: unknown) => {
  return [toText(partnerId), toText(contractManageId), toText(contractMemberId), toText(memberNo)].join("|");
};

const toMemberIndex = (members: MemberContractManageModel[]) => {
  const memberByKey = new Map<string, MemberContractManageModel>();

  members.forEach((member) => {
    memberByKey.set(toMatchKey(member.partnerId, member.contractManageId, member.contractMemberId, member.memberNo), member);
  });

  return memberByKey;
};

const findMember = (memberByKey: Map<string, MemberContractManageModel>, row: UserDeleteRow) => {
  return memberByKey.get(toMatchKey(row.partnerId, row.contractManageId, row.contractMemberId, row.memberNo));
};

/**
 * 사람을 특정하는 세 ID 중 하나라도 비어 있는지.
 *
 * 빈 값끼리도 문자열로는 일치하므로, 이 검사가 없으면 ID 를 지운 줄이 대조를 통과할 수 있다.
 *
 * **회원번호는 여기 없다** — 미가입(초대 상태) 이용자는 회원번호 자체가 없어 양식에 `-` 로 내려가고,
 * 그 줄도 삭제할 수 있어야 한다(QA-3702). 회원번호를 *틀리게* 적은 줄은 대조에서 걸린다.
 */
const hasEmptyIdentifier = (row: UserDeleteRow) => {
  return !row.partnerId || !row.contractManageId || !row.contractMemberId;
};

/**
 * 표시 3개 컬럼(닉네임·휴대폰번호·이메일)에 **서버가 실을 수 있는 값들.** 앞에 있는 것이 양식에
 * 나가는 값이고, 대조는 이 중 하나와만 맞으면 통과한다.
 *
 * 미가입(초대 상태) 이용자는 가입 계정이 없어 휴대폰번호·이메일이 비어 있다. 초대에 쓴 연락처로
 * 대신 채운다 — 닉네임까지 셋이 모두 `-` 면 목록에서 누가 누군지 구분할 수 없다.
 */
const toDisplayCandidates = (member: MemberContractManageModel) => {
  return {
    nickname: [member.nickname],
    phoneNumber: [member.phoneNumber, member.inviteMobileNumber],
    email: [member.email, member.inviteEmail],
  };
};

/** 양식·결과 엑셀에 싣는 값. 서버가 아무것도 안 주면 `-` */
const toDisplayValue = (candidates: (string | undefined)[]) => {
  return orEmptyMark(candidates.find((candidate) => !!candidate));
};

/**
 * 셀 값이 **지금 서버 상태로 설명되는지.**
 *
 * 다운로드와 업로드는 별개로 도는 절차다 — 양식을 언제 어느 언어로 받았는지, 그때 이 컬럼에 어느
 * 필드를 실었는지는 보지 않는다. 업로드 시점에 다시 조회한 명단만 근거로 삼는다.
 *
 * 서버 값이 하나도 없으면 양식이 `-` 로 채워 내려준 자리이므로 `-` 만 받는다.
 */
const isServerValue = (cell: string, candidates: (string | undefined)[]) => {
  const values = candidates.filter((candidate) => !!candidate).map((candidate) => toText(candidate));

  return values.length === 0 ? cell === EMPTY_MARK : values.includes(cell);
};

/**
 * 표시용 3개 컬럼이 서버가 아는 값에서 벗어났는지 컬럼별로 확인한다.
 *
 * 세 ID 와 회원번호는 대조로 이미 걸러진다 — 어긋나면 매칭 자체가 실패한다. 남는 건 이 셋인데,
 * 닉네임·휴대폰번호·이메일은 동시에 여러 개가 어긋날 수 있어 사유도 컬럼 수만큼 따로 담는다.
 */
const toEditedDisplayFieldReasons = (row: UserDeleteRow, member: MemberContractManageModel): UserDeleteReasonItem[] => {
  const candidates = toDisplayCandidates(member);
  const reasons: UserDeleteReasonItem[] = [];

  if (!isServerValue(row.nickname, candidates.nickname)) {
    reasons.push({ reason: USER_DELETE_REASON.NICKNAME_MISMATCH });
  }
  if (!isServerValue(row.phoneNumber, candidates.phoneNumber)) {
    reasons.push({ reason: USER_DELETE_REASON.PHONE_MISMATCH });
  }
  if (!isServerValue(row.email, candidates.email)) {
    reasons.push({ reason: USER_DELETE_REASON.EMAIL_MISMATCH });
  }

  return reasons;
};

/** 명단 대조 + 잔여 건수를 사유로 옮겨 행의 다음 상태를 만든다 */
const toJudgedRow = (
  row: UserDeleteRow,
  memberByKey: Map<string, MemberContractManageModel>,
  remainingByMemberNo: Map<string, MemberRemaining>,
  selfMemberNo?: string,
): UserDeleteRow => {
  const matched = hasEmptyIdentifier(row) ? undefined : findMember(memberByKey, row);

  // 식별 값이 비었거나 계약에 없는 사람은 잔여를 따질 대상 자체가 아니다
  if (hasEmptyIdentifier(row) || !matched) {
    return { ...row, state: USER_DELETE_ROW_STATE.BLOCKED, reasons: [{ reason: USER_DELETE_REASON.NOT_IN_CONTRACT }] };
  }

  /**
   * 표시 값이 어긋나도 **누구인지는 이미 알아냈다** — 신원은 네 ID 대조로 정해지고 이 셋은 부가 정보다.
   * 그래서 여기서 끊지 않고 사유만 담아 아래 판정을 마저 돌린다. 끊어버리면 같은 행에 걸린
   * 계약자·본인 계정·잔여 건수가 닉네임 오류 하나에 가려진다 (QA-3732).
   */
  const editedFieldReasons = toEditedDisplayFieldReasons(row, matched);
  const isEdited = editedFieldReasons.length > 0;

  // 양식을 손댄 줄로는 대상을 확정하지 않는다. 삭제 요청을 만들려면 이 값이 있어야 하므로
  // 사유와 별개로 여기서 한 번 더 막힌다
  const judged = { ...row, deleteTargetId: isEdited ? undefined : matched.contractMemberId };
  const reasons: UserDeleteReasonItem[] = [...editedFieldReasons];

  // 로그인한 이용자 마스터 본인 계정은 스스로를 지울 수 없다. 회원번호가 숫자로 내려오는 응답도 있어 문자열로 맞춰 비교한다
  const selfMemberNoText = toText(selfMemberNo);
  const isSelf = !!selfMemberNoText && toText(matched.memberNo) === selfMemberNoText;
  if (isSelf) {
    reasons.push({ reason: USER_DELETE_REASON.SELF_ACCOUNT });
  }

  if (matched.isContractor) {
    reasons.push({ reason: USER_DELETE_REASON.CONTRACTOR });
  }
  if (matched.isDeleted) {
    reasons.push({ reason: USER_DELETE_REASON.ALREADY_DELETED });
  }

  // 회원번호가 없으면 예약도 방문자 초대도 만들 수 없다. 판정 루프가 조회를 건너뛴 행이라 0건으로 본다
  const remaining = row.memberNo ? remainingByMemberNo.get(row.memberNo) : NO_REMAINING;

  // 잔여를 확인하지 못했으면 삭제 가능으로 볼 수 없다. 기획 사유 목록에 없어 '삭제 처리 오류'를 쓴다
  if (!remaining || remaining.isFailed) {
    reasons.push({ reason: USER_DELETE_REASON.PROCESS_ERROR });
  } else {
    if (remaining.reservationCount > 0) {
      reasons.push({ reason: USER_DELETE_REASON.REMAINING_RESERVATION, count: remaining.reservationCount });
    }
    if (remaining.visitorCount > 0) {
      reasons.push({ reason: USER_DELETE_REASON.REMAINING_VISITOR, count: remaining.visitorCount });
    }
  }

  if (reasons.length === 0) {
    return { ...judged, state: USER_DELETE_ROW_STATE.SELECTABLE, reasons };
  }

  // 잔여 예약·방문자만 걸렸으면 개별 삭제로 풀 수 있다
  const isResolvable = reasons.every(({ reason }) => {
    return reason === USER_DELETE_REASON.REMAINING_RESERVATION || reason === USER_DELETE_REASON.REMAINING_VISITOR;
  });

  return { ...judged, state: isResolvable ? USER_DELETE_ROW_STATE.RESOLVABLE : USER_DELETE_ROW_STATE.BLOCKED, reasons };
};

// ════════ 엑셀 파싱 ════════

const PARSE_RESULT = {
  SUCCESS: "SUCCESS",
  EMPTY: "EMPTY", // 헤더는 맞는데 데이터 행이 하나도 없음
  INVALID_HEADER: "INVALID_HEADER",
  OVER_LIMIT: "OVER_LIMIT",
} as const;

type ParseResult =
  | { type: typeof PARSE_RESULT.SUCCESS; rows: UserDeleteRow[] }
  | { type: typeof PARSE_RESULT.EMPTY }
  | { type: typeof PARSE_RESULT.INVALID_HEADER }
  | { type: typeof PARSE_RESULT.OVER_LIMIT; count: number };

/** 손상된 xlsx 는 read 에서 throw 한다. 이 기능에서 axios 바깥으로 예외가 새는 유일한 지점 */
const readSheet = async (file: File): Promise<xlsx.WorkSheet | null> => {
  try {
    const buffer = await file.arrayBuffer();
    const workbook = xlsx.read(buffer, { type: "array" });

    return workbook.Sheets[workbook.SheetNames[0]] || null;
  } catch {
    return null;
  }
};

/**
 * **고정 위치 파싱.** 헤더 문자열은 보지 않고 앞에서부터 7개 열을 양식 순서 그대로 읽는다.
 *
 * 양식은 8열이지만 마지막 상태 열은 읽지 않는다 — 서버가 아는 값을 보여주기만 하는 칸이라
 * 대조에도 판정에도 쓰이지 않는다. 상태 열을 쓰게 되면 이 인덱스 목록에 8번째를 더한다.
 *
 * **`TEMPLATE_COLUMNS` 순서를 바꾸면 이 인덱스도 같이 바꾼다.** 어긋나도 예외는 나지 않는다 —
 * 셀 값이 엉뚱한 필드로 들어가 4키 대조가 전부 실패하고, 명단 전체가 판정에서
 * `NOT_IN_CONTRACT`(계약에 존재하지 않는 이용자)로 떨어진다. 잘못된 대상이 삭제되지는 않지만
 * 화면에는 "전원 삭제 불가" 로만 보인다.
 */
const parseSheet = (sheet: xlsx.WorkSheet): ParseResult => {
  const matrix = xlsx.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: true, defval: "" });

  // 양식 컬럼 순서 = 파트너 ID · 계약관리 ID · 회원번호 · 이용자 ID · 닉네임 · 휴대폰번호 · 이메일 · (상태)
  const [partnerIdAt, contractManageIdAt, memberNoAt, contractMemberIdAt, nicknameAt, phoneNumberAt, emailAt] = [0, 1, 2, 3, 4, 5, 6];
  const rows: UserDeleteRow[] = [];

  matrix.slice(1).forEach((cells, index) => {
    const isBlankRow = cells.every((cell) => !toText(cell));
    if (isBlankRow) {
      return;
    }

    rows.push({
      rowId: String(index + 2), // 엑셀 행 번호 (헤더가 1행)
      partnerId: toText(cells[partnerIdAt]),
      contractManageId: toText(cells[contractManageIdAt]),
      contractMemberId: toText(cells[contractMemberIdAt]),
      // 미가입(초대 상태) 이용자는 회원번호가 없어 양식에 `-` 로 내려간다. 그 줄도 삭제 대상이다
      memberNo: fromEmptyMark(toText(cells[memberNoAt])),
      nickname: toText(cells[nicknameAt]),
      phoneNumber: toText(cells[phoneNumberAt]),
      email: toText(cells[emailAt]),
      state: USER_DELETE_ROW_STATE.SELECTABLE,
      reasons: [],
    });
  });

  // 중복 행을 여기서 버리지 않는다. 기획이 "결과 엑셀은 업로드한 파일과 동일" 을 요구하므로
  // 업로드한 줄은 그대로 남겨야 1:1 대응이 유지된다. 같은 사람이 여러 줄이면 삭제 루프가
  // 첫 줄에서 처리하고 나머지는 그 결과만 따라간다 (startDelete 참고)
  if (rows.length === 0) {
    return { type: PARSE_RESULT.EMPTY };
  }
  if (rows.length > MAX_UPLOAD_ROWS) {
    return { type: PARSE_RESULT.OVER_LIMIT, count: rows.length };
  }

  return { type: PARSE_RESULT.SUCCESS, rows };
};

// ════════ API 파라미터 ════════

const getBaseTime = () => moment().format(YmdFormat.WITH_TIME_ZONE);

const toReservationParams = (contractId: number | string, memberNo: string, baseTime: string, size: number): ContractReservationListParams => {
  return {
    contractId: String(contractId),
    organizerMemberNo: [memberNo],
    statusCode: RESERVATION_STATUS_CODES,
    greaterThanEqualsEnd: baseTime,
    page: 0,
    size,
  };
};

const toVisitorParams = (contractManageId: number | string, memberNo: string, baseTime: string, size: number): VisitorListParams => {
  return {
    inviteeMemberNo: memberNo,
    contractManageId,
    contractManageIds: [contractManageId],
    visitStatuses: VISIT_STATUSES,
    searchStartTime: baseTime,
    searchEndTime: NO_END_TIME,
    page: 0,
    size,
  };
};

// ════════ 개별 삭제 모달 ════════

/**
 * 시안 흐름: 본 모달 → 잔여 삭제 확인 → 다시 본 모달(잔여가 0으로 갱신됨) → 삭제.
 * 완료 모달은 시안에 없다 — 확인 모달이 닫히면 본 모달의 건수가 바뀌며 삭제 버튼이 열린다.
 */
const INDIVIDUAL_STEP = {
  MAIN: "MAIN", // 본 모달
  PROCESSING: "PROCESSING", // 본 모달을 열어둔 채 버튼만 잠금
  CONFIRM_RESERVATION: "CONFIRM_RESERVATION",
  CONFIRM_VISITOR: "CONFIRM_VISITOR",
  CONFIRM_RETRY: "CONFIRM_RETRY", // 삭제를 다시 시도할까요?
  DELETING: "DELETING", // 모달을 닫고 행 체크박스 자리에서 진행
  DONE: "DONE", // 삭제를 완료했어요
  FAILED: "FAILED",
} as const;

type IndividualStep = typeof INDIVIDUAL_STEP[keyof typeof INDIVIDUAL_STEP];

/** rowId 가 빈 문자열이면 모달이 닫힌 상태다 */
type UserDeleteIndividual = {
  rowId: string;
  step: IndividualStep;
  reservationCount: number;
  visitorCount: number;
};

const CLOSED_INDIVIDUAL: UserDeleteIndividual = {
  rowId: "",
  step: INDIVIDUAL_STEP.MAIN,
  reservationCount: 0,
  visitorCount: 0,
};

// ════════ 화면 데이터 ════════

type UserDeleteScreen = {
  phase: UserDeletePhase;
  fileName: string;
  done: number; // CTA 진행 표시의 분자 — **처리 중인 순번**이지 완료 건수가 아니다
  total: number; // 처리 요청 총 건수
  individual: UserDeleteIndividual;
  isSubmitModalOpen: boolean; // 일괄 삭제 더블체크
  isLeaveModalOpen: boolean; // 삭제 중 이탈 방지
  alertMessage: string;
};

const INITIAL_SCREEN: UserDeleteScreen = {
  phase: USER_DELETE_PHASE.IDLE,
  fileName: "",
  done: 0,
  total: 0,
  individual: CLOSED_INDIVIDUAL,
  isSubmitModalOpen: false,
  isLeaveModalOpen: false,
  alertMessage: "",
};

const SCREEN_EVENT = {
  UPLOADED: "UPLOADED",
  PROGRESS_RESET: "PROGRESS_RESET",
  PROGRESSED: "PROGRESSED",
  JUDGED: "JUDGED",
  JUDGE_FAILED: "JUDGE_FAILED",
  SUBMIT_MODAL_TOGGLED: "SUBMIT_MODAL_TOGGLED",
  DELETE_STARTED: "DELETE_STARTED",
  DELETE_FINISHED: "DELETE_FINISHED",
  LEAVE_MODAL_TOGGLED: "LEAVE_MODAL_TOGGLED",
  ALERTED: "ALERTED",
  INDIVIDUAL_OPENED: "INDIVIDUAL_OPENED",
  INDIVIDUAL_CLOSED: "INDIVIDUAL_CLOSED",
  INDIVIDUAL_REQUESTED: "INDIVIDUAL_REQUESTED",
  INDIVIDUAL_STEP_MOVED: "INDIVIDUAL_STEP_MOVED",
  INDIVIDUAL_REMAINING_LOADED: "INDIVIDUAL_REMAINING_LOADED",
  INDIVIDUAL_FAILED: "INDIVIDUAL_FAILED",
} as const;

type ScreenEvent =
  | { type: typeof SCREEN_EVENT.UPLOADED; fileName: string }
  | { type: typeof SCREEN_EVENT.PROGRESS_RESET; total: number }
  | { type: typeof SCREEN_EVENT.PROGRESSED }
  | { type: typeof SCREEN_EVENT.JUDGED }
  | { type: typeof SCREEN_EVENT.JUDGE_FAILED }
  | { type: typeof SCREEN_EVENT.SUBMIT_MODAL_TOGGLED; isOpen: boolean }
  | { type: typeof SCREEN_EVENT.DELETE_STARTED; total: number }
  | { type: typeof SCREEN_EVENT.DELETE_FINISHED }
  | { type: typeof SCREEN_EVENT.LEAVE_MODAL_TOGGLED; isOpen: boolean }
  | { type: typeof SCREEN_EVENT.ALERTED; message: string }
  | { type: typeof SCREEN_EVENT.INDIVIDUAL_OPENED; rowId: string; step: IndividualStep }
  | { type: typeof SCREEN_EVENT.INDIVIDUAL_CLOSED }
  | { type: typeof SCREEN_EVENT.INDIVIDUAL_REQUESTED }
  | { type: typeof SCREEN_EVENT.INDIVIDUAL_STEP_MOVED; step: IndividualStep }
  | ({ type: typeof SCREEN_EVENT.INDIVIDUAL_REMAINING_LOADED } & Remaining)
  | { type: typeof SCREEN_EVENT.INDIVIDUAL_FAILED };

/**
 * 화면 단계 전이가 전부 여기 모여 있다. 출발 단계가 맞지 않는 이벤트는 그대로 돌려보내므로,
 * 이탈·재업로드 뒤 늦게 도착한 응답이 지나간 단계를 되살리지 못한다.
 */
const screenReducer = (screen: UserDeleteScreen, event: ScreenEvent): UserDeleteScreen => {
  switch (event.type) {
    case SCREEN_EVENT.UPLOADED: {
      const isBusy = screen.phase === USER_DELETE_PHASE.VALIDATING || screen.phase === USER_DELETE_PHASE.DELETING;
      if (isBusy) {
        return screen;
      }

      return { ...INITIAL_SCREEN, phase: USER_DELETE_PHASE.VALIDATING, fileName: event.fileName };
    }

    case SCREEN_EVENT.PROGRESS_RESET: {
      // 판정·삭제 두 루프가 같은 진행률 칸을 쓴다. 순회할 건수가 정해지는 시점에 분모를 채운다
      const isRunning = screen.phase === USER_DELETE_PHASE.VALIDATING || screen.phase === USER_DELETE_PHASE.DELETING;
      if (!isRunning) {
        return screen;
      }

      return { ...screen, done: 0, total: event.total };
    }

    case SCREEN_EVENT.PROGRESSED: {
      const isRunning = screen.phase === USER_DELETE_PHASE.VALIDATING || screen.phase === USER_DELETE_PHASE.DELETING;
      if (!isRunning) {
        return screen;
      }

      return { ...screen, done: screen.done + 1 };
    }

    case SCREEN_EVENT.JUDGED: {
      if (screen.phase !== USER_DELETE_PHASE.VALIDATING) {
        return screen;
      }

      return { ...screen, phase: USER_DELETE_PHASE.SELECTING };
    }

    case SCREEN_EVENT.JUDGE_FAILED: {
      if (screen.phase !== USER_DELETE_PHASE.VALIDATING) {
        return screen;
      }

      return { ...INITIAL_SCREEN };
    }

    case SCREEN_EVENT.SUBMIT_MODAL_TOGGLED: {
      if (screen.phase !== USER_DELETE_PHASE.SELECTING) {
        return screen;
      }

      return { ...screen, isSubmitModalOpen: event.isOpen };
    }

    case SCREEN_EVENT.DELETE_STARTED: {
      if (screen.phase !== USER_DELETE_PHASE.SELECTING) {
        return screen;
      }

      return { ...screen, phase: USER_DELETE_PHASE.DELETING, isSubmitModalOpen: false, done: 0, total: event.total };
    }

    case SCREEN_EVENT.DELETE_FINISHED: {
      if (screen.phase !== USER_DELETE_PHASE.DELETING) {
        return screen;
      }

      return { ...screen, phase: USER_DELETE_PHASE.RESULT };
    }

    case SCREEN_EVENT.LEAVE_MODAL_TOGGLED:
      return { ...screen, isLeaveModalOpen: event.isOpen };

    case SCREEN_EVENT.ALERTED:
      return { ...screen, alertMessage: event.message };

    case SCREEN_EVENT.INDIVIDUAL_OPENED:
      // 잔여를 읽으러 가면 PROCESSING, 재시도면 곧바로 확인 모달
      return { ...screen, individual: { ...CLOSED_INDIVIDUAL, rowId: event.rowId, step: event.step } };

    case SCREEN_EVENT.INDIVIDUAL_CLOSED:
      return { ...screen, individual: CLOSED_INDIVIDUAL };

    case SCREEN_EVENT.INDIVIDUAL_REQUESTED:
      return { ...screen, individual: { ...screen.individual, step: INDIVIDUAL_STEP.PROCESSING } };

    case SCREEN_EVENT.INDIVIDUAL_STEP_MOVED:
      return { ...screen, individual: { ...screen.individual, step: event.step } };

    case SCREEN_EVENT.INDIVIDUAL_REMAINING_LOADED: {
      const { reservationCount, visitorCount } = event;

      return { ...screen, individual: { ...screen.individual, step: INDIVIDUAL_STEP.MAIN, reservationCount, visitorCount } };
    }

    case SCREEN_EVENT.INDIVIDUAL_FAILED:
      return { ...screen, individual: { ...screen.individual, step: INDIVIDUAL_STEP.FAILED } };
  }
};

// ════════ 사유 문구 ════════

const useReasonText = () => {
  const { t } = useTranslation("user");

  return useCallback(
    (row: UserDeleteRow, phase: UserDeletePhase): string => {
      // 이름 붙은 함수로 둬야 switch 가 모든 갈래를 덮는 걸 lint 도 인정한다
      const toLabel = ({ reason, count = 0 }: UserDeleteReasonItem): string => {
        switch (reason) {
          case USER_DELETE_REASON.CONTRACTOR:
            return t("deleteUser.reasonContractor");
          case USER_DELETE_REASON.SELF_ACCOUNT:
            return t("deleteUser.reasonSelfAccount");
          case USER_DELETE_REASON.NOT_IN_CONTRACT:
            return t("deleteUser.reasonNotInContract");
          case USER_DELETE_REASON.NICKNAME_MISMATCH:
            return t("deleteUser.reasonNicknameMismatch");
          case USER_DELETE_REASON.PHONE_MISMATCH:
            return t("deleteUser.reasonPhoneMismatch");
          case USER_DELETE_REASON.EMAIL_MISMATCH:
            return t("deleteUser.reasonEmailMismatch");
          case USER_DELETE_REASON.ALREADY_DELETED:
            return t("deleteUser.reasonAlreadyDeleted");
          case USER_DELETE_REASON.REMAINING_RESERVATION:
            return t("deleteUser.reasonRemainingReservation", { count });
          case USER_DELETE_REASON.REMAINING_VISITOR:
            return t("deleteUser.reasonRemainingVisitor", { count });
          case USER_DELETE_REASON.EXCLUDED:
            return t("deleteUser.reasonExcluded");
          case USER_DELETE_REASON.PROCESS_ERROR:
            return t("deleteUser.reasonProcessError");
        }
      };

      return toDisplayReasons(row, phase).map(toLabel).join(" · ");
    },
    [t],
  );
};

// ════════ 엑셀 문구 ════════

/**
 * 엑셀에 싣는 문구는 **화면 언어와 무관하게 한·영을 병기한다.**
 *
 * 그래서 i18n 을 타지 않는다 — ko/en 리소스에 같은 문자열을 두 벌 두는 꼴이 된다.
 * 화면에 그리는 사유 배너는 지금대로 화면 언어를 따른다 (`useReasonText`).
 */
const EXCEL_HEADER = {
  PARTNER_ID: "파트너 ID (Partner ID)",
  CONTRACT_MANAGE_ID: "계약관리 ID (Contract Manage ID)",
  MEMBER_NO: "회원번호 (Member Number)",
  CONTRACT_MEMBER_ID: "이용자 ID (User ID)",
  NICKNAME: "닉네임 (Nickname)",
  PHONE_NUMBER: "휴대폰번호 (Mobile Number)",
  EMAIL: "이메일 (Email)",
  STATUS: "상태 (Status)",
  RESULT: "삭제결과 (Deletion Result)",
  REASON: "사유 (Reason)",
} as const;

/**
 * 이용자 상태 문구. 상태가 늘면 조회하는 자리에서 컴파일 에러가 나므로 여기만 채우면 된다.
 *
 * `contract-types` 의 `getMemberStatusText` 는 쓰지 않는다 — 병기가 아닌 한국어 단독 문구다.
 */
const EXCEL_MEMBER_STATUS_TEXT = {
  MEMBER_INVITE: "초대 (Invited)",
  MEMBER_USE: "사용 (Active)",
  MEMBER_DELETE: "삭제 (Deleted)",
  MEMBER_CLOSE: "사용종료 (Inactive)",
  MEMBER_EXPIRE: "초대만료 (Invitation Expired)",
} as const;

const EXCEL_RESULT_TEXT = {
  DONE: "완료 (Completed)",
  NOT_DONE: "미완료 (Incomplete)",
} as const;

// 저장 파일명과 시트명에 함께 쓰는 이름. 헤더와 달리 괄호·공백은 쓰지 않는다 — 파일명에 그대로 남는 문자다
const EXCEL_NAME = {
  TEMPLATE: "이용자명단_UserList",
  RESULT: "이용자삭제결과_UserDeletionResult",
} as const;

// 확장자만 붙인 저장 이름. 시트명과 갈라지지 않게 EXCEL_NAME 에서 파생한다
const EXCEL_FILE_NAME = {
  TEMPLATE: `${EXCEL_NAME.TEMPLATE}.xlsx`,
  RESULT: `${EXCEL_NAME.RESULT}.xlsx`,
} as const;

/** 결과 엑셀의 사유 컬럼. 문구만 병기일 뿐 고르는 규칙은 화면 배너와 같다 */
const toExcelReasonText = (row: UserDeleteRow): string => {
  // 이름 붙은 함수로 둬야 switch 가 모든 갈래를 덮는 걸 lint 도 인정한다
  const toLabel = ({ reason, count = 0 }: UserDeleteReasonItem): string => {
    switch (reason) {
      case USER_DELETE_REASON.CONTRACTOR:
        return "계약자 (Contract Holder)";
      case USER_DELETE_REASON.SELF_ACCOUNT:
        return "본인 계정 직접 삭제 (Cannot Delete Own Account)";
      case USER_DELETE_REASON.NOT_IN_CONTRACT:
        return "계약에 존재하지 않는 이용자 (User not part of this contract)";
      case USER_DELETE_REASON.NICKNAME_MISMATCH:
        return "입력한 닉네임 오류 (Incorrect Nickname)";
      case USER_DELETE_REASON.PHONE_MISMATCH:
        return "입력한 휴대폰번호 오류 (Incorrect Mobile Number)";
      case USER_DELETE_REASON.EMAIL_MISMATCH:
        return "입력한 이메일 오류 (Incorrect Email)";
      case USER_DELETE_REASON.ALREADY_DELETED:
        return "이미 삭제된 이용자 (User already deleted)";
      case USER_DELETE_REASON.REMAINING_RESERVATION:
        return `잔여 공용공간 예약 ${count}건 (${count} remaining shared space reservation(s))`;
      case USER_DELETE_REASON.REMAINING_VISITOR:
        return `잔여 방문자 초대 ${count}건 (${count} remaining visitor invitation(s))`;
      case USER_DELETE_REASON.EXCLUDED:
        return "삭제 대상 제외 (Excluded from deletion)";
      case USER_DELETE_REASON.PROCESS_ERROR:
        return "삭제 처리 오류 (Deletion processing error)";
    }
  };

  return toDisplayReasons(row, USER_DELETE_PHASE.RESULT).map(toLabel).join(" · ");
};

// ════════ 엑셀 컬럼 ════════

// 헤더 문구가 곧 업로드 파일의 컬럼 식별자라 양식·결과·파싱이 같은 값을 봐야 한다
const TEMPLATE_COLUMNS: ExcelColumn<MemberContractManageModel>[] = [
  { header: EXCEL_HEADER.PARTNER_ID, value: (member) => member.partnerId },
  { header: EXCEL_HEADER.CONTRACT_MANAGE_ID, value: (member) => member.contractManageId },
  { header: EXCEL_HEADER.MEMBER_NO, value: (member) => orEmptyMark(member.memberNo) },
  { header: EXCEL_HEADER.CONTRACT_MEMBER_ID, value: (member) => member.contractMemberId },
  { header: EXCEL_HEADER.NICKNAME, value: (member) => toDisplayValue(toDisplayCandidates(member).nickname) },
  { header: EXCEL_HEADER.PHONE_NUMBER, value: (member) => toDisplayValue(toDisplayCandidates(member).phoneNumber) },
  { header: EXCEL_HEADER.EMAIL, value: (member) => toDisplayValue(toDisplayCandidates(member).email) },
  // 읽기 전용 8번째 열. 업로드 때 파싱하지 않으므로 대조·판정에는 쓰이지 않는다
  { header: EXCEL_HEADER.STATUS, value: (member) => (member.memberStatus ? EXCEL_MEMBER_STATUS_TEXT[member.memberStatus] : EMPTY_MARK) },
];

/**
 * 앞 7개 컬럼은 업로드 파일과 동일하고 결과·사유 두 개가 붙는다.
 * 양식 8번째의 상태는 싣지 않는다 — 업로드 때 파싱하지 않아 행이 그 값을 들고 있지 않다.
 *
 * **양식 컬럼 순서를 바꾸면 여기도 같이 바꾼다.** 이 목록은 `TEMPLATE_COLUMNS` 를 참조하지 않고
 * 같은 헤더 상수를 각자 집어 쓸 뿐이라, 어긋나도 컴파일이 잡아주지 않는다.
 */
const RESULT_COLUMNS: ExcelColumn<UserDeleteRow>[] = [
  { header: EXCEL_HEADER.PARTNER_ID, value: (row) => row.partnerId },
  { header: EXCEL_HEADER.CONTRACT_MANAGE_ID, value: (row) => row.contractManageId },
  { header: EXCEL_HEADER.MEMBER_NO, value: (row) => orEmptyMark(row.memberNo) },
  { header: EXCEL_HEADER.CONTRACT_MEMBER_ID, value: (row) => row.contractMemberId },
  { header: EXCEL_HEADER.NICKNAME, value: (row) => orEmptyMark(row.nickname) },
  { header: EXCEL_HEADER.PHONE_NUMBER, value: (row) => orEmptyMark(row.phoneNumber) },
  { header: EXCEL_HEADER.EMAIL, value: (row) => orEmptyMark(row.email) },
  {
    header: EXCEL_HEADER.RESULT,
    // 이번 루프에서 실제로 지운 건만 '완료' 다. 전부터 지워져 있던 건은 미완료 + 사유로 남는다
    value: (row) => (row.state === USER_DELETE_ROW_STATE.DELETED ? EXCEL_RESULT_TEXT.DONE : EXCEL_RESULT_TEXT.NOT_DONE),
  },
  { header: EXCEL_HEADER.REASON, value: toExcelReasonText },
];

// ════════ 행 컴포넌트 ════════

/**
 * 행별 구독이 있어야 삭제 루프가 한 건을 갈아끼울 때 그 행만 다시 그려진다.
 * 훅은 컴포넌트 경계에서만 걸 수 있으므로 이 조각만은 별도 컴포넌트로 남는다.
 */
type RowItemProps = {
  store: RowStore;
  rowId: string;
  phase: UserDeletePhase;
  onToggleCheck: (rowId: string) => void;
  onClickIndividual: (rowId: string) => void;
};

const UserDeleteRowItem = ({ store, rowId, phase, onToggleCheck, onClickIndividual }: RowItemProps) => {
  const { t } = useTranslation("user");
  const getReasonText = useReasonText();
  const row = useRow(store, rowId);

  if (!row) {
    return null;
  }

  const isSelecting = phase === USER_DELETE_PHASE.SELECTING;
  const isDeleted = showsDeletedMark(row);
  const isFailed = row.state === USER_DELETE_ROW_STATE.FAILED;
  const isJudgeBlocked = row.state === USER_DELETE_ROW_STATE.RESOLVABLE || row.state === USER_DELETE_ROW_STATE.BLOCKED;
  const reasonText = getReasonText(row, phase);

  let stateClass = "";
  if (row.state === USER_DELETE_ROW_STATE.PROCESSING) {
    stateClass = "--processing";
  } else if (isDeleted) {
    stateClass = "--deleted";
  } else if (isFailed) {
    stateClass = "--failed";
  } else if (isJudgeBlocked) {
    stateClass = "--disabled";
  }

  const isChecked = row.state === USER_DELETE_ROW_STATE.SELECTABLE || ATTEMPTED_STATES.includes(row.state);
  const isCheckboxDisabled = !isSelecting || !(row.state === USER_DELETE_ROW_STATE.SELECTABLE || row.state === USER_DELETE_ROW_STATE.EXCLUDED);

  // 대기·삭제 중·삭제완료는 전부 왼쪽 체크박스 자리의 모양으로 나타낸다 (시안). 행에 상태 글자를 두지 않는다
  const fields = [
    { label: t("deleteUser.fieldNickname"), value: orEmptyMark(row.nickname) },
    { label: t("deleteUser.fieldPhoneNumber"), value: orEmptyMark(row.phoneNumber) },
    { label: t("deleteUser.fieldEmail"), value: orEmptyMark(row.email) },
  ];

  return (
    // 목록 항목(li)은 가상 스크롤 칸이 맡는다. 여기는 그 안을 채우는 내용물이다
    <div className={`user-delete-item ${stateClass}`.trim()}>
      <BaseCheckbox
        id={`user-delete-${row.rowId}`}
        name="user-delete"
        className="user-delete-item__check"
        checked={isChecked}
        disabled={isCheckboxDisabled}
        onChange={() => onToggleCheck(row.rowId)}
      >
        <dl className="user-delete-item__fields">
          {fields.map(({ label, value }) => (
            <div key={label} className="user-delete-item__field">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </BaseCheckbox>

      {reasonText && (
        <div className="user-delete-item__reason">
          <p className="user-delete-item__reason-text">{reasonText}</p>
          {hasIndividualButton(row, phase) && (
            <button
              type="button"
              className="base-btn color-white size-xsmall user-delete-item__reason-btn"
              onClick={() => onClickIndividual(row.rowId)}
            >
              <span>{t("deleteUser.individualDelete")}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ════════ 화면 ════════

/**
 * 마이 > 신청/계약 > 이용자 목록 > 이용자 엑셀 일괄 삭제
 */
const UserDelete = () => {
  const { t } = useTranslation(["user", "common"]);
  const { contractId } = useParams();
  const { goBack } = useNavigateGoBack();
  const { downloadExcel } = useExcelDownload();
  const { member } = useMemberInfoState();

  const { executeAsync: getContract } = useApiOperation(getContractAsync);
  const { executeAsync: getMemberList } = useApiOperation(getMemberContractManageListAsync, REQUEST_OPTIONS);
  const { executeAsync: getReservationList } = useApiOperation(getReservationListAsync, REQUEST_OPTIONS);
  const { executeAsync: getVisitorList } = useApiOperation(getVisitorListAsync, REQUEST_OPTIONS);
  const { executeAsync: deleteReservation } = useApiOperation(deleteReservationsAsync, REQUEST_OPTIONS);
  const { executeAsync: deleteVisitors } = useApiOperation(deleteVisitorsAsync, REQUEST_OPTIONS);
  const { executeAsync: patchMemberDelete } = useApiOperation(patchMemberContractManageDeleteAsync, REQUEST_OPTIONS);

  // 저장소는 페이지와 함께 죽는다. 지연 초기화하지 않으면 렌더마다 새로 만들어 버린다
  const storeRef = useRef<RowStore>();
  if (!storeRef.current) {
    storeRef.current = createRowStore();
  }
  const store = storeRef.current;

  const [screen, dispatch] = useReducer(screenReducer, INITIAL_SCREEN);
  const summary = useSummary(store);

  const [contract, setContract] = useState<ContractModel | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * 오래 도는 작업(판정 순회·삭제 루프·양식 다운로드)의 취소 신호.
   *
   * 컴포넌트 수명을 따라가는 불리언 플래그로 두면 "언제 되살리는가" 가 숨은 규칙이 되고,
   * effect 정리 한 번에 전체가 중단 상태로 굳는다. 대신 작업이 시작될 때마다 새 컨트롤러를
   * 만들고, 이전 작업은 그 자리에서 끊는다. 언마운트는 현재 작업만 끊으면 된다.
   */
  const workRef = useRef<AbortController | null>(null);

  const startWork = useCallback(() => {
    workRef.current?.abort();

    const controller = new AbortController();
    workRef.current = controller;

    return controller.signal;
  }, []);

  useEffect(() => {
    return () => {
      workRef.current?.abort();
    };
  }, []);

  // 모달을 닫은 뒤 늦게 도착한 응답이 다시 모달을 열지 못하게 한다
  const individualRowIdRef = useRef<string>("");
  const isIndividualStale = useCallback((rowId: string) => individualRowIdRef.current !== rowId, []);

  useEffect(() => {
    if (!contractId) {
      return;
    }

    const fetchContract = async () => {
      const response = await getContract({ contractId });
      setContract(response?.data?.data || null);
    };

    fetchContract();
  }, [contractId, getContract]);

  const showAlert = useCallback((message: string) => dispatch({ type: SCREEN_EVENT.ALERTED, message }), []);
  const clearAlert = useCallback(() => dispatch({ type: SCREEN_EVENT.ALERTED, message: "" }), []);

  // ---- 공통 조회 ----

  const fetchAllMembers = useCallback(
    async (signal: AbortSignal, isAllMember?: boolean) => {
      if (!contract?.contractManageId) {
        return null;
      }

      const contractManageId = contract.contractManageId;

      const members = await fetchAllPages<MemberContractManageModel[], MemberContractManageModel>(
        (page) =>
          getMemberList({
            contractManageId,
            isAllMember,
            pageRequest: {
              page,
              size: MEMBER_PAGE_SIZE,
              // 계약의 이용자 정렬 기준. 계약자 > 이용자 마스터 > 이용자 순, 같은 등급 안에서는 최근 초대순.
              sort: MEMBER_SORT,
            },
          }),
        (data) => data || [],
        signal,
      );

      if (!members) {
        return null;
      }

      // 클라이언트 정렬로직 일단 만들어만 두고 사용은 상황 봐서
      // return sortMembers(members);

      return members;
    },
    [contract, getMemberList],
  );

  // 모달을 여는 시점엔 아직 individual.rowId 가 반영되기 전이라 회원번호를 인자로 받는다
  const fetchReservations = useCallback(
    async (memberNo: string) => {
      if (!contract?.contractId) {
        return null;
      }

      // 회원번호가 비면 필터가 빈 값으로 나가 계약 전체가 걸린다. toQueryParams 가
      // [""] 를 파라미터 생략이 아니라 빈 문자열로 직렬화하기 때문이다.
      // 초대 상태(MEMBER_INVITE) 이용자는 memberNo 가 없으므로 조회 없이 0건으로 본다
      if (!memberNo) {
        return [];
      }

      const response = await getReservationList(toReservationParams(contract.contractId, memberNo, getBaseTime(), RESOLVE_PAGE_SIZE));

      return isResponseOk(response) ? response?.data?.data?.content || [] : null;
    },
    [contract, getReservationList],
  );

  const fetchVisitorIds = useCallback(
    async (memberNo: string) => {
      if (!contract?.contractManageId) {
        return null;
      }

      // fetchReservations 와 같은 이유
      if (!memberNo) {
        return [];
      }

      const response = await getVisitorList(toVisitorParams(contract.contractManageId, memberNo, getBaseTime(), RESOLVE_PAGE_SIZE));
      if (!isResponseOk(response)) {
        return null;
      }

      return (response?.data?.data?.contractVisitor || [])
        .map((visitor) => visitor.contractVisitorId)
        .filter((id): id is ContractVisitorId => id !== undefined && id !== null);
    },
    [contract, getVisitorList],
  );

  const fetchRemaining = useCallback(
    async (memberNo: string): Promise<Remaining | null> => {
      const [reservations, visitorIds] = await Promise.all([fetchReservations(memberNo), fetchVisitorIds(memberNo)]);

      if (!reservations || !visitorIds) {
        return null;
      }

      return { reservationCount: reservations.length, visitorCount: visitorIds.length };
    },
    [fetchReservations, fetchVisitorIds],
  );

  const deleteMember = useCallback(
    (contractMemberId: number) => {
      if (!contract?.contractManageId) {
        return Promise.resolve(undefined);
      }

      return patchMemberDelete({ contractManageId: contract.contractManageId, contractMemberId, isDeleted: true });
    },
    [contract, patchMemberDelete],
  );

  // ---- 엑셀 ----

  const downloadTemplate = useCallback(async () => {
    if (isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      // 삭제할 이용자를 고르는 양식이라 이미 삭제된 이용자는 넣지 않는다
      const members = await fetchAllMembers(startWork());

      if (!members) {
        showAlert(t("deleteUser.downloadFailed"));
        return;
      }
      if (members.length === 0) {
        showAlert(t("deleteUser.downloadEmpty"));
        return;
      }

      downloadExcel({
        columns: TEMPLATE_COLUMNS,
        rows: members,
        fileName: EXCEL_FILE_NAME.TEMPLATE,
        sheetName: EXCEL_NAME.TEMPLATE,
        webViewFileName: EXCEL_FILE_NAME.TEMPLATE,
      });
    } finally {
      setIsDownloading(false);
    }
  }, [downloadExcel, fetchAllMembers, isDownloading, showAlert, startWork, t]);

  const downloadResult = useCallback(() => {
    const rows = store
      .getRowIds()
      .map((rowId) => store.getRow(rowId))
      .filter((row): row is UserDeleteRow => row !== undefined);

    downloadExcel({
      columns: RESULT_COLUMNS,
      rows,
      fileName: EXCEL_FILE_NAME.RESULT,
      sheetName: EXCEL_NAME.RESULT,
      webViewFileName: EXCEL_FILE_NAME.RESULT,
    });
  }, [downloadExcel, store]);

  // ---- 업로드 → 판정 ----

  const uploadFile = useCallback(
    async (file: File) => {
      const sheet = await readSheet(file);
      if (!sheet) {
        showAlert(t("deleteUser.uploadInvalidHeader"));
        return;
      }

      const parsed = parseSheet(sheet);

      if (parsed.type === PARSE_RESULT.INVALID_HEADER) {
        showAlert(t("deleteUser.uploadInvalidHeader"));
        return;
      }
      if (parsed.type === PARSE_RESULT.EMPTY) {
        showAlert(t("deleteUser.uploadEmpty"));
        return;
      }
      if (parsed.type === PARSE_RESULT.OVER_LIMIT) {
        // 다섯 자리 숫자라 콤마 없이는 읽히지 않는다. count 는 i18next 복수형 예약어라 쓰지 않는다
        showAlert(
          t("deleteUser.uploadOverLimit", {
            max: numberToStringWithComma(MAX_UPLOAD_ROWS),
            current: numberToStringWithComma(parsed.count),
          }),
        );
        return;
      }
      if (!contract?.contractId || !contract?.contractManageId) {
        showAlert(t("deleteUser.judgeFailed"));
        return;
      }

      const { contractId: targetContractId, contractManageId } = contract;

      // 이전 업로드가 아직 돌고 있으면 여기서 끊긴다
      const signal = startWork();

      store.setRows(parsed.rows);
      dispatch({ type: SCREEN_EVENT.UPLOADED, fileName: file.name });

      // 잘린 목록으로 대조하면 있는 사람을 "계약에 존재하지 않는 이용자" 로 오판한다
      const members = await fetchAllMembers(signal, true);
      if (signal.aborted) {
        return;
      }
      if (!members) {
        dispatch({ type: SCREEN_EVENT.JUDGE_FAILED });
        showAlert(t("deleteUser.judgeFailed"));
        return;
      }

      const memberByKey = toMemberIndex(members);

      // 계약에 있는 사람만 잔여를 확인한다. 없는 사람은 사유가 이미 정해져 있다
      const matchedMemberNos = Array.from(
        new Set(parsed.rows.filter((row) => Boolean(row.memberNo) && Boolean(findMember(memberByKey, row))).map((row) => row.memberNo)),
      );

      dispatch({ type: SCREEN_EVENT.PROGRESS_RESET, total: matchedMemberNos.length });

      const baseTime = getBaseTime();
      const remainingByMemberNo = new Map<string, MemberRemaining>();

      for (const memberNo of matchedMemberNos) {
        if (signal.aborted) {
          return;
        }

        const [reservationCount, visitorCount] = await Promise.all([
          getReservationList(toReservationParams(targetContractId, memberNo, baseTime, COUNT_PAGE_SIZE)).then(readTotalCount),
          getVisitorList(toVisitorParams(contractManageId, memberNo, baseTime, COUNT_PAGE_SIZE)).then(readTotalCount),
        ]);

        // 조회 실패는 0과 다르다. 0으로 뭉개면 "잔여 없음"으로 오판해 삭제 대상에 넣어버린다
        const isFailed = reservationCount === null || visitorCount === null;

        remainingByMemberNo.set(memberNo, {
          reservationCount: reservationCount ?? 0,
          visitorCount: visitorCount ?? 0,
          isFailed,
        });

        dispatch({ type: SCREEN_EVENT.PROGRESSED });
      }

      if (signal.aborted) {
        return;
      }

      const judged = new Map<string, UserDeleteRow>();
      parsed.rows.forEach((row) => {
        judged.set(row.rowId, toJudgedRow(row, memberByKey, remainingByMemberNo, member?.memberNo));
      });

      store.patchRows(judged);
      dispatch({ type: SCREEN_EVENT.JUDGED });
    },
    [contract, fetchAllMembers, getReservationList, getVisitorList, member, showAlert, startWork, store, t],
  );

  const changeFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      // 같은 파일을 다시 선택할 수 있도록 input 값을 비운다
      event.target.value = "";

      if (file) {
        uploadFile(file);
      }
    },
    [uploadFile],
  );

  // ---- 2단계 선택 ----

  const toggleCheck = useCallback(
    (rowId: string) => {
      const row = store.getRow(rowId);
      if (!row) {
        return;
      }

      if (row.state === USER_DELETE_ROW_STATE.SELECTABLE) {
        store.setRow(rowId, { ...row, state: USER_DELETE_ROW_STATE.EXCLUDED });
        return;
      }
      if (row.state === USER_DELETE_ROW_STATE.EXCLUDED) {
        store.setRow(rowId, { ...row, state: USER_DELETE_ROW_STATE.SELECTABLE });
      }
    },
    [store],
  );

  // 잔여를 읽을 때마다 목록의 사유도 같이 맞춘다. 판정 시점의 "공용공간 예약 3건" 이 지운 뒤에도
  // 목록에 남아 있으면 화면이 거짓말을 한다. 삭제 루프의 실패 건 재조회와 개별 삭제 모달이 함께 쓴다
  const syncRowRemaining = useCallback(
    (rowId: string, remaining: Remaining) => {
      const row = store.getRow(rowId);
      if (!row) {
        return;
      }

      store.setRow(rowId, withRemainingReasons(row, remaining));
    },
    [store],
  );

  // ---- 3단계 삭제 루프 ----

  const startDelete = useCallback(async () => {
    // 대상 선별은 저장소 밖에서 한다. 개별 삭제로 이미 지운 행·판정에서 막힌 행·체크를 푼 행은
    // SELECTABLE 이 아니라서 애초에 들어오지 않는다.
    //
    // 같은 이용자가 여러 줄이면 그만큼 요청이 나간다. **동일인 묶음은 FE 가 판단하지 않는다** —
    // 기획에 중복 처리 규정이 없어 임의 정책을 만들지 않는다. 두 번째 요청은 서버가 거절한다
    const targets = store
      .getRowIds()
      .map((rowId) => store.getRow(rowId))
      .filter((row): row is DeletableRow => row?.state === USER_DELETE_ROW_STATE.SELECTABLE && typeof row.deleteTargetId === "number");

    if (targets.length === 0) {
      return;
    }

    const waiting = new Map<string, UserDeleteRow>();
    targets.forEach((row) => {
      waiting.set(row.rowId, { ...row, state: USER_DELETE_ROW_STATE.WAITING });
    });

    const signal = startWork();

    store.patchRows(waiting);
    dispatch({ type: SCREEN_EVENT.DELETE_STARTED, total: targets.length });

    const failed: DeletableRow[] = [];

    for (const target of targets) {
      if (signal.aborted) {
        return;
      }

      store.setRow(target.rowId, { ...target, state: USER_DELETE_ROW_STATE.PROCESSING });
      // CTA 의 n 은 "지금 처리 중인 순번" 이라 요청 전에 올린다.
      // 완료 건수는 저장소가 따로 세며 상시 요약이 그 값을 쓴다
      dispatch({ type: SCREEN_EVENT.PROGRESSED });

      const response = await deleteMember(target.deleteTargetId);
      const isDeleted = isResponseOk(response);

      if (signal.aborted) {
        return;
      }

      if (!isDeleted) {
        failed.push(target);
      }

      store.setRow(target.rowId, {
        ...target,
        // 삭제에 성공했으면 판정 사유는 의미가 없어진다
        reasons: isDeleted ? [] : target.reasons,
        state: isDeleted ? USER_DELETE_ROW_STATE.DELETED : USER_DELETE_ROW_STATE.FAILED,
      });
    }

    // 판정 이후 삭제 사이에 예약·초대가 생겨 거절된 건은 판정 당시 사유가 비어 있어 이유를 알 수 없다.
    // 실패한 행만 잔여를 다시 읽어 사유를 채운다 — 그래야 결과 화면이 '삭제 처리 오류' 대신 실제 사유를
    // 보여주고, 개별 삭제도 재시도가 아니라 해소 모달로 들어간다.
    // 결과 화면이 뜬 뒤에 사유가 바뀌면 화면이 한 번 흔들리므로 DELETE_FINISHED 전에 끝낸다
    for (const target of failed) {
      if (signal.aborted) {
        return;
      }

      // 회원번호가 없는 초대 상태 이용자는 예약도 초대도 만들 수 없어 조회 없이 0건으로 돌아온다
      const remaining = await fetchRemaining(target.memberNo);
      if (signal.aborted) {
        return;
      }
      // 재조회마저 실패하면 이유를 모르는 것이므로 '삭제 처리 오류' 를 그대로 둔다
      if (!remaining) {
        continue;
      }

      syncRowRemaining(target.rowId, remaining);
    }

    if (signal.aborted) {
      return;
    }

    dispatch({ type: SCREEN_EVENT.DELETE_FINISHED });
  }, [deleteMember, fetchRemaining, startWork, store, syncRowRemaining]);

  // ---- 개별 삭제 ----

  const openIndividual = useCallback(
    async (rowId: string) => {
      const row = store.getRow(rowId);
      if (!row) {
        return;
      }

      individualRowIdRef.current = rowId;

      // 잔여가 걸린 건은 해소 모달을 우선 노출한다. 그 외(실패·제외)는 곧바로 재시도 확인이다
      if (!hasRemainingReason(row)) {
        dispatch({ type: SCREEN_EVENT.INDIVIDUAL_OPENED, rowId, step: INDIVIDUAL_STEP.CONFIRM_RETRY });
        return;
      }

      dispatch({ type: SCREEN_EVENT.INDIVIDUAL_OPENED, rowId, step: INDIVIDUAL_STEP.PROCESSING });

      const remaining = await fetchRemaining(row.memberNo);
      if (isIndividualStale(rowId)) {
        return;
      }
      if (!remaining) {
        dispatch({ type: SCREEN_EVENT.INDIVIDUAL_FAILED });
        return;
      }

      syncRowRemaining(rowId, remaining);
      dispatch({ type: SCREEN_EVENT.INDIVIDUAL_REMAINING_LOADED, ...remaining });
    },
    [fetchRemaining, isIndividualStale, store, syncRowRemaining],
  );

  const closeIndividual = useCallback(() => {
    individualRowIdRef.current = "";
    dispatch({ type: SCREEN_EVENT.INDIVIDUAL_CLOSED });
  }, []);

  const moveIndividualStep = useCallback((step: IndividualStep) => dispatch({ type: SCREEN_EVENT.INDIVIDUAL_STEP_MOVED, step }), []);

  const individualRowId = screen.individual.rowId;
  const individualMemberNo = (individualRowId ? store.getRow(individualRowId)?.memberNo : "") || "";

  const cancelReservations = useCallback(async () => {
    if (!contract?.contractId) {
      return;
    }

    const targetContractId = String(contract.contractId);
    dispatch({ type: SCREEN_EVENT.INDIVIDUAL_REQUESTED });

    const reservations = await fetchReservations(individualMemberNo);
    if (isIndividualStale(individualRowId)) {
      return;
    }
    if (!reservations) {
      dispatch({ type: SCREEN_EVENT.INDIVIDUAL_FAILED });
      return;
    }

    const cancel = (reservationId: string) => deleteReservation({ contractId: targetContractId, reservationId });
    const results = await Promise.all(reservations.map((reservation) => cancel(String(reservation.id))));

    // 실패한 건만 한 번 더 시도한다
    const failed = reservations.filter((_, index) => !isResponseOk(results[index]));
    const retried = await Promise.all(failed.map((reservation) => cancel(String(reservation.id))));

    const isCanceled = retried.every((response) => isResponseOk(response));
    const remaining = isCanceled ? await fetchRemaining(individualMemberNo) : null;

    if (isIndividualStale(individualRowId)) {
      return;
    }
    if (!remaining) {
      dispatch({ type: SCREEN_EVENT.INDIVIDUAL_FAILED });
      return;
    }

    // 시안에 완료 모달이 없다. 본 모달로 돌아가면 잔여가 0으로 바뀌며 삭제 버튼이 열린다
    syncRowRemaining(individualRowId, remaining);
    dispatch({ type: SCREEN_EVENT.INDIVIDUAL_REMAINING_LOADED, ...remaining });
  }, [contract, deleteReservation, fetchRemaining, fetchReservations, individualMemberNo, individualRowId, isIndividualStale, syncRowRemaining]);

  const cancelVisitors = useCallback(async () => {
    dispatch({ type: SCREEN_EVENT.INDIVIDUAL_REQUESTED });

    const contractVisitorIds = await fetchVisitorIds(individualMemberNo);
    if (isIndividualStale(individualRowId)) {
      return;
    }
    if (!contractVisitorIds) {
      dispatch({ type: SCREEN_EVENT.INDIVIDUAL_FAILED });
      return;
    }

    if (contractVisitorIds.length > 0) {
      const response = await deleteVisitors({ inviteeMemberNo: individualMemberNo, contractVisitorIds });
      if (isIndividualStale(individualRowId)) {
        return;
      }
      if (!isResponseOk(response)) {
        dispatch({ type: SCREEN_EVENT.INDIVIDUAL_FAILED });
        return;
      }
    }

    const remaining = await fetchRemaining(individualMemberNo);
    if (isIndividualStale(individualRowId)) {
      return;
    }
    if (!remaining) {
      dispatch({ type: SCREEN_EVENT.INDIVIDUAL_FAILED });
      return;
    }

    syncRowRemaining(individualRowId, remaining);
    dispatch({ type: SCREEN_EVENT.INDIVIDUAL_REMAINING_LOADED, ...remaining });
  }, [deleteVisitors, fetchRemaining, fetchVisitorIds, individualMemberNo, individualRowId, isIndividualStale, syncRowRemaining]);

  const deleteIndividual = useCallback(async () => {
    const rowId = individualRowId;
    const target = store.getRow(rowId);
    if (!target?.deleteTargetId) {
      return;
    }

    // 시안: 모달을 닫고 행 체크박스 자리에 스피너를 돌린다. 하단 CTA 도 "삭제 중 1/1" 로 바뀐다
    moveIndividualStep(INDIVIDUAL_STEP.DELETING);
    store.setRow(rowId, { ...target, state: USER_DELETE_ROW_STATE.PROCESSING });

    const response = await deleteMember(target.deleteTargetId);
    if (isIndividualStale(rowId)) {
      return;
    }
    if (!isResponseOk(response)) {
      // 실패하면 원래 상태로 되돌린다. 결과는 모달이 알린다
      store.setRow(rowId, target);
      dispatch({ type: SCREEN_EVENT.INDIVIDUAL_FAILED });
      return;
    }

    // 모달을 여는 동안 잔여 사유가 갱신됐을 수 있어 저장소에서 다시 읽는다.
    // 잔여를 해소하고 지웠으므로 사유는 남기지 않는다
    const latest = store.getRow(rowId) || target;

    store.setRow(rowId, { ...latest, state: USER_DELETE_ROW_STATE.DELETED, reasons: [] });
    moveIndividualStep(INDIVIDUAL_STEP.DONE);
  }, [deleteMember, individualRowId, isIndividualStale, moveIndividualStep, store]);

  // ---- 이탈 방지 ----

  const clickClose = useCallback(() => {
    if (screen.phase === USER_DELETE_PHASE.DELETING) {
      dispatch({ type: SCREEN_EVENT.LEAVE_MODAL_TOGGLED, isOpen: true });
      return;
    }

    goBack();
  }, [goBack, screen.phase]);

  const confirmLeave = useCallback(() => {
    // 이미 지운 건은 그대로 남고 대기 중인 건은 루프가 멈춰 미처리로 남는다 (부분 삭제)
    workRef.current?.abort();
    dispatch({ type: SCREEN_EVENT.LEAVE_MODAL_TOGGLED, isOpen: false });
    goBack();
  }, [goBack]);

  const cancelLeave = useCallback(() => dispatch({ type: SCREEN_EVENT.LEAVE_MODAL_TOGGLED, isOpen: false }), []);

  // ---- 파생값 ----

  const { phase, individual } = screen;
  const isValidating = phase === USER_DELETE_PHASE.VALIDATING;
  const isSelecting = phase === USER_DELETE_PHASE.SELECTING;
  const isDeleting = phase === USER_DELETE_PHASE.DELETING;
  const isResult = phase === USER_DELETE_PHASE.RESULT;
  const hasList = isSelecting || isDeleting || isResult;

  const rowIds = store.getRowIds();

  /**
   * 스크롤 주체가 window 가 아니다. 웹뷰(`body.open-taap`)에서는 `.base-layout-container` 가
   * `overflow: auto` 로 스크롤을 맡고 `body` 는 `overflow: hidden` 이다. window 기준으로
   * 가상화하면 스크롤이 영원히 0이라 첫 화면 분량 뒤의 행이 마운트되지 않는다 —
   * 명단 끝 몇 명이 그냥 사라진다.
   *
   * 환경마다 스크롤 컨테이너가 달라지므로 이름으로 찍지 않고 실제로 스크롤하는 조상을 찾는다.
   */
  const [scroll, setScroll] = useState<{ element: HTMLElement | null; margin: number }>({ element: null, margin: 0 });

  useLayoutEffect(() => {
    if (!hasList) {
      return;
    }

    const measure = () => {
      const list = listRef.current;
      if (!list) {
        return;
      }

      let element: HTMLElement | null = list.parentElement;
      while (element) {
        if (/(auto|scroll)/.test(window.getComputedStyle(element).overflowY)) {
          break;
        }
        element = element.parentElement;
      }

      // 스크롤하는 조상이 없으면 문서 자체가 스크롤한다
      const scroller = element || (document.scrollingElement as HTMLElement | null);
      if (!scroller) {
        return;
      }

      // 목록 위에 업로드 영역·안내가 있다. 그 높이만큼이 가상 좌표의 출발점이다
      const margin = list.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;

      setScroll((prev) => (prev.element === scroller && prev.margin === margin ? prev : { element: scroller, margin }));
    };

    measure();
    window.addEventListener("resize", measure);

    return () => window.removeEventListener("resize", measure);
  }, [hasList, phase]);

  const virtualizer = useVirtualizer({
    count: hasList ? rowIds.length : 0,
    getScrollElement: () => scroll.element,
    estimateSize: () => ROW_ESTIMATED_HEIGHT,
    overscan: 6,
    scrollMargin: scroll.margin,
  });

  // 계약 상세를 받기 전에는 어떤 API 도 부를 수 없다. 그 전에 올리면 판정이 곧바로 실패한다
  const isContractReady = Boolean(contract?.contractId) && Boolean(contract?.contractManageId);

  // 개별 삭제가 도는 동안은 모달이 닫혀 있고 하단 CTA 가 진행 표시로 바뀐다
  const isDeletingRow = individual.step === INDIVIDUAL_STEP.DELETING;
  const isIndividualOpen = Boolean(individual.rowId);
  const isIndividualProcessing = individual.step === INDIVIDUAL_STEP.PROCESSING;
  const hasRemaining = individual.reservationCount > 0 || individual.visitorCount > 0;

  /**
   * 진행 요약 토스트. 첫 건이 지워지면 떠서 삭제가 도는 내내 건수를 갱신하다가,
   * **마지막 건의 처리가 끝난 뒤** 잠깐 남았다 스스로 사라진다 (QA-3719).
   *
   * 삭제 중에는 타이머를 걸지 않는다 — 건수마다 1초를 재면 느린 한 건 때문에 진행 표시가
   * 중간에 꺼진다. 도는 동안은 그냥 띄워두고, 루프가 멈춘 뒤에야 시계를 돌린다.
   */
  const [isSummaryToastOpen, setIsSummaryToastOpen] = useState<boolean>(false);
  const { deletedCount } = summary;
  const isDeleteRunning = isDeleting || isDeletingRow;

  useEffect(() => {
    // 재업로드로 명단이 갈리면 건수가 0으로 돌아온다. 지난 삭제의 토스트를 남겨둘 이유가 없다
    if (deletedCount === 0) {
      setIsSummaryToastOpen(false);

      return;
    }

    setIsSummaryToastOpen(true);

    if (isDeleteRunning) {
      return;
    }

    const timer = setTimeout(() => setIsSummaryToastOpen(false), SUMMARY_TOAST_LINGER_MS);

    return () => clearTimeout(timer);
  }, [deletedCount, isDeleteRunning]);

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header headerType="CLOSE" onClickCloseButton={clickClose} />

      <div className="user-delete-page">
        <input ref={fileRef} onChange={changeFile} type="file" name="file" accept=".xlsx, .numbers" hidden />

        <section className="user-delete-upload">
          <h1 className="user-delete-title">{t("deleteUser.title")}</h1>
          <ul className="base-list user-delete-guide">
            <li>{t("deleteUser.guideDownload")}</li>
            <li>{t("deleteUser.guideUpload")}</li>
          </ul>

          <h2 className="user-delete-step">{t("deleteUser.step1")}</h2>
          <div className="user-delete-file">
            <p className="user-delete-file__name">{t("deleteUser.guideFileName")}</p>
            <button
              type="button"
              className="base-btn color-white size-small user-delete-file__btn --download"
              disabled={!isContractReady || isDownloading || isValidating}
              onClick={downloadTemplate}
            >
              <span>{t("deleteUser.download")}</span>
            </button>
          </div>

          <h2 className="user-delete-step">{t("deleteUser.step2")}</h2>
          <ul className="base-list user-delete-guide">
            <li>{t("deleteUser.guideUploadLimit", { max: RECOMMENDED_UPLOAD_ROWS })}</li>
          </ul>
          <div className="user-delete-file">
            {/* 파일을 골라도 파일명으로 바뀌지 않는다 — 시안은 첨부 전후 모두 `* 첨부파일 (n/1)` 한 줄이다 */}
            <p className="user-delete-file__attachment required">{t("deleteUser.attachment", { current: screen.fileName ? 1 : 0, max: 1 })}</p>
            <button
              type="button"
              className="base-btn color-white size-small user-delete-file__btn --upload"
              disabled={!isContractReady || isValidating}
              onClick={() => fileRef.current?.click()}
            >
              <span>{t("deleteUser.selectFile")}</span>
            </button>
          </div>

          {isValidating && <p className="user-delete-progress">{t("deleteUser.validating", { done: screen.done, total: screen.total })}</p>}
        </section>

        {hasList && (
          <section className="user-delete-result">
            <p className="user-delete-count">{t("deleteUser.userCount", { count: summary.total })}</p>
            <ul className="base-list user-delete-guide">
              <li>{t("deleteUser.guideCannotDelete")}</li>
              <li>{t("deleteUser.guideRemaining")}</li>
              <li>{t("deleteUser.guideResult")}</li>
            </ul>

            {/* 결과 화면 요약. 배지와 건수가 분리돼 있다 (시안) — 둘이 명단 전체를 나눠 가진다 */}
            {isResult && (
              <div className="user-delete-summary">
                {summary.deletedCount > 0 && (
                  <span className="user-delete-summary__item">
                    <em className="user-delete-summary__badge --deleted">{t("deleteUser.statusDeleted")}</em>
                    {t("deleteUser.countWithUnit", { count: summary.deletedCount })}
                  </span>
                )}
                {summary.total - summary.deletedCount > 0 && (
                  <span className="user-delete-summary__item">
                    <em className="user-delete-summary__badge --blocked">{t("deleteUser.statusNonDeletable")}</em>
                    {t("deleteUser.countWithUnit", { count: summary.total - summary.deletedCount })}
                  </span>
                )}
              </div>
            )}

            {/* 목록만 가상화한다. 스크롤 주체는 window 가 아니라 위에서 찾은 조상이다 */}
            <div ref={listRef} className="user-delete-list">
              <ul className="user-delete-list__inner" style={{ height: `${virtualizer.getTotalSize()}px` }}>
                {virtualizer.getVirtualItems().map((virtualItem) => (
                  // 가상 스크롤이 자리를 잡는 칸이 곧 목록 항목이다. ul 의 직계 자식은 li 여야 한다
                  <li
                    key={rowIds[virtualItem.index]}
                    className="user-delete-list__slot"
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{ transform: `translateY(${virtualItem.start - virtualizer.options.scrollMargin}px)` }}
                  >
                    <UserDeleteRowItem
                      store={store}
                      rowId={rowIds[virtualItem.index]}
                      phase={phase}
                      onToggleCheck={toggleCheck}
                      onClickIndividual={openIndividual}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/*
          삭제 진행 요약. CTA 위에 얹히는 토스트다. 개별 삭제로 한 건만 지워도 뜨고,
          마지막 건이 끝나면 잠깐 남았다 스스로 사라진다 (위 isSummaryToastOpen 참고)
        */}
        {hasList && isSummaryToastOpen && (
          <div className="user-delete-summary-toast">{t("deleteUser.deletingProgress", { done: deletedCount, total: summary.total })}</div>
        )}

        {isSelecting && !isDeletingRow && (
          <div className="base-floating-btn-wrap">
            <button
              type="button"
              className="base-btn"
              disabled={summary.selectableCount === 0}
              onClick={() => dispatch({ type: SCREEN_EVENT.SUBMIT_MODAL_TOGGLED, isOpen: true })}
            >
              {summary.selectableCount > 0 ? t("deleteUser.submitWithCount", { count: summary.selectableCount }) : t("deleteUser.submit")}
            </button>
          </div>
        )}

        {(isDeleting || isDeletingRow) && (
          <div className="base-floating-btn-wrap">
            <button type="button" className="base-btn user-delete-cta" disabled>
              <Spinner size="sm" />
              {t("deleteUser.deleting", isDeletingRow ? { done: 1, total: 1 } : { done: screen.done, total: screen.total })}
            </button>
          </div>
        )}

        {isResult && !isDeletingRow && (
          <div className="base-floating-btn-wrap">
            <button type="button" className="base-btn user-delete-download-cta" onClick={downloadResult}>
              {t("deleteUser.downloadResult")}
            </button>
          </div>
        )}

        {/* 일괄 삭제 더블체크 */}
        <ConfirmModal
          isOpen={screen.isSubmitModalOpen}
          btnLeftTitle={t("common:cancel")}
          btnRightTitle={t("deleteUser.delete")}
          onClose={() => dispatch({ type: SCREEN_EVENT.SUBMIT_MODAL_TOGGLED, isOpen: false })}
          onClick={startDelete}
        >
          <div className="user-delete-modal">
            <p className="user-delete-modal__title">{t("deleteUser.deleteConfirm", { count: summary.selectableCount })}</p>
            <p className="user-delete-modal__desc">{t("deleteUser.deleteConfirmDesc")}</p>
          </div>
        </ConfirmModal>

        {/* 삭제 진행 중 이탈 */}
        <ConfirmModal
          isOpen={screen.isLeaveModalOpen}
          btnLeftTitle={t("deleteUser.leaveExit")}
          btnRightTitle={t("deleteUser.leaveContinue")}
          onClose={confirmLeave}
          onClick={cancelLeave}
        >
          <div className="user-delete-modal">
            <p className="user-delete-modal__title">{t("deleteUser.leaveTitle")}</p>
            <p className="user-delete-modal__desc">{t("deleteUser.leaveDesc")}</p>
          </div>
        </ConfirmModal>

        {/* 개별 삭제 — 본 모달. 잔여가 남아 있으면 삭제 버튼이 잠긴다 */}
        <ConfirmModal
          className="minmax350 px20"
          isOpen={isIndividualOpen && (individual.step === INDIVIDUAL_STEP.MAIN || isIndividualProcessing)}
          btnLeftTitle={t("common:cancel")}
          btnRightTitle={t("userList.delete")}
          btnRightDisable={hasRemaining || isIndividualProcessing}
          onClose={closeIndividual}
          onClick={deleteIndividual}
        >
          <div className="user-delete-modal">
            <p className="user-delete-modal__title">{t("userList.deleteUser")}</p>
            <p className="user-delete-modal__desc">
              <Trans t={t} i18nKey="userList.remainingReservationDesc" components={{ br: <br /> }} />
            </p>

            <div className="user-delete-remaining">
              <span className="user-delete-remaining__label">{t("userList.remainingReservation", { count: individual.reservationCount })}</span>
              <button
                type="button"
                className="user-delete-remaining__btn"
                disabled={individual.reservationCount === 0 || isIndividualProcessing}
                onClick={() => moveIndividualStep(INDIVIDUAL_STEP.CONFIRM_RESERVATION)}
              >
                {t("userList.delete")}
              </button>
            </div>

            <div className="user-delete-remaining">
              <span className="user-delete-remaining__label">{t("userList.remainingVisitor", { count: individual.visitorCount })}</span>
              <button
                type="button"
                className="user-delete-remaining__btn"
                disabled={individual.visitorCount === 0 || isIndividualProcessing}
                onClick={() => moveIndividualStep(INDIVIDUAL_STEP.CONFIRM_VISITOR)}
              >
                {t("userList.delete")}
              </button>
            </div>

            <p className="user-delete-modal__desc">
              {t("userList.deleteImmediateDesc1")}
              <br />
              {t("userList.deleteImmediateDesc2")}
            </p>
          </div>
        </ConfirmModal>

        {/* 잔여 공용공간 예약 삭제 확인 */}
        <ConfirmModal
          isOpen={individual.step === INDIVIDUAL_STEP.CONFIRM_RESERVATION}
          btnLeftTitle={t("common:cancel")}
          btnRightTitle={t("userList.delete")}
          onClose={() => moveIndividualStep(INDIVIDUAL_STEP.MAIN)}
          onClick={cancelReservations}
        >
          <p className="user-delete-modal__title">{t("userList.deleteReservationConfirm")}</p>
        </ConfirmModal>

        {/* 잔여 방문자 초대 삭제 확인 */}
        <ConfirmModal
          isOpen={individual.step === INDIVIDUAL_STEP.CONFIRM_VISITOR}
          btnLeftTitle={t("common:cancel")}
          btnRightTitle={t("userList.delete")}
          onClose={() => moveIndividualStep(INDIVIDUAL_STEP.MAIN)}
          onClick={cancelVisitors}
        >
          <p className="user-delete-modal__title">{t("userList.deleteVisitorConfirm")}</p>
        </ConfirmModal>

        {/* 삭제 실패·제외 건 재시도. 잔여가 걸린 건은 위 해소 모달로 간다 */}
        <ConfirmModal
          isOpen={individual.step === INDIVIDUAL_STEP.CONFIRM_RETRY}
          btnLeftTitle={t("common:cancel")}
          btnRightTitle={t("deleteUser.retryConfirm")}
          onClose={closeIndividual}
          onClick={deleteIndividual}
        >
          <div className="user-delete-modal">
            <p className="user-delete-modal__title">{t("deleteUser.retryTitle")}</p>
            <p className="user-delete-modal__desc">{t("deleteUser.deleteConfirmDesc")}</p>
          </div>
        </ConfirmModal>

        <ConfirmModal isOpen={individual.step === INDIVIDUAL_STEP.DONE} btnRightTitle={t("common:confirm")} onClick={closeIndividual}>
          <p className="user-delete-modal__title">{t("deleteUser.individualDeleteCompleted")}</p>
        </ConfirmModal>

        <ConfirmModal isOpen={individual.step === INDIVIDUAL_STEP.FAILED} btnRightTitle={t("common:confirm")} onClick={closeIndividual}>
          <p className="user-delete-modal__title">{t("deleteUser.individualDeleteFailed")}</p>
        </ConfirmModal>

        {/* 단문 알림 — 확인 버튼 하나 */}
        <ConfirmModal isOpen={Boolean(screen.alertMessage)} btnRightTitle={t("common:confirm")} onClick={clearAlert}>
          <p className="user-delete-modal__title">{screen.alertMessage}</p>
        </ConfirmModal>
      </div>
    </>
  );
};

export default UserDelete;
